import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Calendar, DollarSign, Edit, ListFilter, Loader2, Package, Plus, Search, ShieldPlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return day && month && year ? `${day}/${month}/${year}` : value;
}

function validateMontagemForm(modalState) {
  if (!modalState.data) return { error: 'Informe a data da montagem' };
  const quantidadeCestas = parseInt(modalState.quantidade_cestas || '0', 10);
  const quantidadeKits = parseInt(modalState.quantidade_kits_higiene || '0', 10);
  const valor = parseFloat(String(modalState.valor || '0').replace(',', '.'));
  if (quantidadeCestas < 0 || quantidadeKits < 0 || valor < 0) return { error: 'Valores não podem ser negativos' };
  if (quantidadeCestas === 0 && quantidadeKits === 0 && valor === 0) return { error: 'Informe cestas, kits ou valor' };
  return { quantidadeCestas, quantidadeKits, valor };
}

function buildChartData(items) {
  return [...items].sort((a, b) => (a.data || '').localeCompare(b.data || '')).map(item => ({
    data: formatDate(item.data),
    cestas: Number(item.quantidade_cestas ?? item.quantidade ?? 0),
    kits: Number(item.quantidade_kits_higiene || 0),
    valor: Number(item.valor || 0),
  }));
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function MontagemDialog({ modalState, setModalState, onSaved }) {
  const handleSave = async () => {
    const validation = validateMontagemForm(modalState);
    if (validation.error) { toast.error(validation.error); return; }
    try {
      const r = await fetch(`${API}/cestas-basicas${modalState.editingId ? `/${modalState.editingId}` : ''}`, {
        method: modalState.editingId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: modalState.data, quantidade_cestas: validation.quantidadeCestas, quantidade_kits_higiene: validation.quantidadeKits, valor: validation.valor }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao salvar montagem');
      toast.success(modalState.editingId ? 'Montagem atualizada' : 'Montagem adicionada');
      setModalState(prev => ({ ...prev, open: false }));
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Dialog open={modalState.open} onOpenChange={(open) => setModalState(prev => ({ ...prev, open }))}>
      <DialogContent>
        <DialogHeader><DialogTitle>{modalState.editingId ? 'Editar Montagem' : 'Adicionar Montagem'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Data da montagem *</Label>
            <Input type="date" value={modalState.data} onChange={(e) => setModalState(prev => ({ ...prev, data: e.target.value }))} data-testid="cesta-date-input" />
          </div>
          <div>
            <Label>Qtd. de cestas básicas</Label>
            <Input type="number" min="0" value={modalState.quantidade_cestas} onChange={(e) => setModalState(prev => ({ ...prev, quantidade_cestas: e.target.value }))} data-testid="montagem-cestas-input" />
          </div>
          <div>
            <Label>Qtd. de kits de higiene</Label>
            <Input type="number" min="0" value={modalState.quantidade_kits_higiene} onChange={(e) => setModalState(prev => ({ ...prev, quantidade_kits_higiene: e.target.value }))} data-testid="montagem-kits-input" />
          </div>
          <div>
            <Label>Valor (R$)</Label>
            <Input type="number" min="0" step="0.01" value={modalState.valor} onChange={(e) => setModalState(prev => ({ ...prev, valor: e.target.value }))} data-testid="montagem-valor-input" />
          </div>
          <Button type="button" onClick={handleSave} data-testid="cesta-save-button" className="w-full bg-blue-600 hover:bg-blue-700">
            {modalState.editingId ? 'Salvar Alterações' : 'Adicionar Montagem'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MontagemRow({ item, setModalState, onRemoved, formatCurrency }) {
  const cestas = Number(item.quantidade_cestas ?? item.quantidade ?? 0);
  const kits = Number(item.quantidade_kits_higiene || 0);
  const valor = Number(item.valor || 0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const remove = async () => {
    try {
      const r = await fetch(`${API}/cestas-basicas/${item.cesta_id}`, { method: 'DELETE', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao excluir montagem');
      toast.success('Montagem excluída');
      onRemoved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`cesta-row-${item.cesta_id}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        <Calendar className="mt-0.5 w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">Montagem {formatDate(item.data)}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {cestas > 0 && <Badge className="text-xs bg-blue-100 text-blue-700"><Package className="w-3 h-3 mr-1" />{cestas} cestas</Badge>}
            {kits > 0 && <Badge className="text-xs bg-emerald-100 text-emerald-700"><ShieldPlus className="w-3 h-3 mr-1" />{kits} kits</Badge>}
            {valor > 0 && <Badge className="text-xs bg-amber-100 text-amber-700">{formatCurrency(valor)}</Badge>}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-slate-400">Registrado por {item.created_by_name || 'usuário do sistema'}</span>
          </div>
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => setModalState({ open: true, editingId: item.cesta_id, data: item.data || '', quantidade_cestas: String(item.quantidade_cestas ?? item.quantidade ?? ''), quantidade_kits_higiene: String(item.quantidade_kits_higiene || ''), valor: String(item.valor || '') })} data-testid={`edit-montagem-${item.cesta_id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setConfirmDeleteOpen(true)} data-testid={`delete-cesta-${item.cesta_id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent data-testid={`delete-montagem-confirm-modal-${item.cesta_id}`}>
          <DialogHeader><DialogTitle>Excluir montagem?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Confirma a exclusão da montagem de {formatDate(item.data)}?</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmDeleteOpen(false)} data-testid={`cancel-delete-montagem-${item.cesta_id}`}>Cancelar</Button>
              <Button type="button" variant="destructive" onClick={remove} data-testid={`confirm-delete-montagem-${item.cesta_id}`}>Excluir</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CestasBasicasTab() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ cestas: 0, kits: 0, valor: 0 });
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, editingId: null, data: '', quantidade_cestas: '', quantidade_kits_higiene: '', valor: '' });
  const [filter, setFilter] = useState('todas');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const r = await fetch(`${API}/cestas-basicas`, { credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao carregar montagens');
      setItems(data.items || []);
      setTotals({ cestas: data.total_cestas || data.total || 0, kits: data.total_kits_higiene || 0, valor: data.total_valor || 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const counts = {
    todas: items.length,
    cestas: items.filter(i => Number(i.quantidade_cestas ?? i.quantidade ?? 0) > 0).length,
    kits: items.filter(i => Number(i.quantidade_kits_higiene || 0) > 0).length,
    valor: items.filter(i => Number(i.valor || 0) > 0).length,
  };
  const filtros = [
    { key: 'todas', label: 'Todas', count: counts.todas, color: 'bg-blue-50 text-blue-600' },
    { key: 'cestas', label: 'Cestas básicas', count: counts.cestas, color: 'bg-blue-50 text-blue-600' },
    { key: 'kits', label: 'Kits de higiene', count: counts.kits, color: 'bg-emerald-50 text-emerald-600' },
    { key: 'valor', label: 'Com valor', count: counts.valor, color: 'bg-amber-50 text-amber-600' },
  ];
  const filteredItems = items.filter(item => {
    const cestas = Number(item.quantidade_cestas ?? item.quantidade ?? 0);
    const kits = Number(item.quantidade_kits_higiene || 0);
    const valor = Number(item.valor || 0);
    if (filter === 'cestas' && cestas <= 0) return false;
    if (filter === 'kits' && kits <= 0) return false;
    if (filter === 'valor' && valor <= 0) return false;
    const term = search.toLowerCase().trim();
    if (!term) return true;
    return `${formatDate(item.data)} ${item.created_by_name || ''} ${cestas} ${kits} ${formatCurrency(valor)}`.toLowerCase().includes(term);
  });
  const chartData = buildChartData(filteredItems);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 overflow-x-hidden" data-testid="montagens-tab">
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Montagens</h2>
                  <Badge className="bg-blue-100 text-blue-700 text-sm font-bold" data-testid="montagens-total-items">{items.length}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por tipo</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="montagens-menu">
              {filtros.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`montagens-filtro-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.color.split(' ')[0]}`}><ListFilter className={`w-4 h-4 ${f.color.split(' ')[1]}`} /></div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-blue-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{f.count}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={() => setModalState({ open: true, editingId: null, data: new Date().toISOString().slice(0, 10), quantidade_cestas: '', quantidade_kits_higiene: '', valor: '' })} data-testid="add-montagem-button" className="w-full bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-1" />Adicionar Montagem
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm overflow-hidden">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{filtros.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filteredItems.length} {filteredItems.length === 1 ? 'registro' : 'registros'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="montagens-search" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {chartData.length > 0 && (
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid="cestas-chart-card">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-slate-900">Métricas por data</p>
                  <p className="text-xs text-slate-500">Data no eixo X; cestas, kits e valor no eixo Y.</p>
                </div>
                <div className="h-64 w-full min-w-0 overflow-hidden" data-testid="montagens-metrics-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="data" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: '#eff6ff' }} formatter={(value, name) => [name === 'valor' ? formatCurrency(value) : value, name === 'cestas' ? 'Cestas básicas' : name === 'kits' ? 'Kits de higiene' : 'Valor (R$)']} labelFormatter={(label) => `Data: ${label}`} />
                      <Bar dataKey="cestas" fill="#2563eb" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="kits" fill="#059669" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="valor" fill="#d97706" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="space-y-0" data-testid="cestas-history-list">
              {loading ? (
                <div className="py-16 flex items-center justify-center" data-testid="cestas-loading"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400" data-testid="cestas-empty-state"><Package className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">Nenhuma montagem registrada ainda.</p></div>
              ) : filteredItems.map(item => <MontagemRow key={item.cesta_id} item={item} setModalState={setModalState} onRemoved={fetchData} formatCurrency={formatCurrency} />)}
            </div>
          </CardContent>
        </Card>
      </div>

      <MontagemDialog modalState={modalState} setModalState={setModalState} onSaved={fetchData} />
    </div>
  );
}